<template>
  <v-card-text class="pt-1 pb-2">
    <v-form ref="form" v-model="isValid" lazy-validation>
      <v-row dense>
        <v-col v-if="imgResp.src" cols="12" align="center">
          <v-img
            :aspect-ratio="aspectRatio"
            :sizes="imgResp.sizes"
            :src="imgResp.src"
            :srcset="imgResp.srcset"
            alt="Image for offer"
            class="mb-2"
            cover
            max-width="160"
          />
        </v-col>

        <v-col v-if="title || description" cols="12">
          <h2 v-if="title" class="text-h5 text-center mb-2">{{ title }}</h2>
          <!-- eslint-disable vue/no-v-html -->
          <div
            v-if="description"
            class="text-body-1"
            v-html="description"
          ></div>
        </v-col>

        <template v-if="step === 0">
          <v-col cols="12">
            <v-row dense>
              <v-col
                cols="12"
                :sm="offer.lead_fields === 'name_email_phone' ? 12 : 6"
              >
                <v-text-field
                  id="offer_form_name"
                  v-model.trim="formData.name"
                  :disabled="isPending"
                  :rules="[requiredRule]"
                  class="mb-2"
                  density="compact"
                  hide-details="auto"
                  label="Name *"
                  variant="outlined"
                />
              </v-col>

              <v-col cols="12" sm="6">
                <v-text-field
                  id="offer_form_email"
                  v-model.trim="formData.email"
                  :disabled="isPending"
                  :rules="[requiredRule, emailRule]"
                  class="mb-2"
                  density="compact"
                  hide-details="auto"
                  label="Email *"
                  type="email"
                  variant="outlined"
                />
              </v-col>

              <v-col
                v-if="offer.lead_fields === 'name_email_phone'"
                cols="12"
                sm="6"
              >
                <v-input
                  :rules="[
                    requiredRule,
                    () => isPhoneValid || 'Not a valid phone'
                  ]"
                  :validation-value="formData.phone"
                  hide-details="auto"
                >
                  <template
                    #default="{
                      isValid: isInputValid,
                      validate: validateInput
                    }"
                  >
                    <div
                      :class="
                        isInputValid.value === null
                          ? 'border rounded border-opacity-50'
                          : isInputValid.value
                            ? 'border rounded border-opacity-50'
                            : 'border rounded border-opacity-100 border-error'
                      "
                      density="compact"
                      style="padding: 7px"
                    >
                      <IntlTelInput
                        :disabled="isPending"
                        :input-props="{
                          onBlur: () => validateInput()
                        }"
                        :options="{
                          autoPlaceholder: 'polite',
                          containerClass: 'd-block offer-content',
                          dropdownContainer: documentBody,
                          fixDropdownWidth: true,
                          initialCountry: 'us',
                          customPlaceholder: (
                            selectedCountryPlaceholder: string
                          ) => selectedCountryPlaceholder + ' *',
                          separateDialCode: true,
                          loadUtils
                        }"
                        @change-number="formData.phone = $event"
                        @change-validity="isPhoneValid = $event"
                      /></div
                  ></template>
                </v-input>
              </v-col>
            </v-row>

            <v-row
              v-if="
                offer.lead_agreement &&
                typeof offer.lead_agreement === 'object' &&
                offer.lead_agreement.status === 'published'
              "
              dense
            >
              <v-col cols="auto">
                <v-checkbox
                  v-model="formData.agreed"
                  :disabled="isPending"
                  :rules="
                    offer.lead_agreement.is_consent_required ? [trueRule] : []
                  "
                  density="compact"
                  hide-details
                ></v-checkbox>
              </v-col>
              <v-col>
                <!-- eslint-disable vue/no-v-html -->
                <div
                  class="text-body-2"
                  v-html="agreementBody(offer.lead_agreement.description)"
                ></div>
              </v-col>
            </v-row>
          </v-col>

          <v-col cols="12">
            <v-btn
              :color="
                offer.color_scheme &&
                typeof offer.color_scheme === 'object' &&
                offer.color_scheme.primary_button_color &&
                typeof offer.color_scheme.primary_button_color === 'object' &&
                offer.color_scheme.primary_button_color.id
                  ? offer.color_scheme.primary_button_color.id
                  : undefined
              "
              :disabled="!isValid || isPending"
              :loading="isPending"
              block
              class="text-none mb-2"
              tile
              variant="flat"
              @click="submit"
              >Submit</v-btn
            >
          </v-col>

          <v-col v-if="isSubmitError" cols="12">
            <v-alert color="error" density="compact" type="error">
              Sorry! There was an error submitting your request. Please try a
              bit later or contact us via email.
            </v-alert>
          </v-col>
        </template>

        <template v-else>
          <v-col cols="12" class="mb-2">
            <v-row v-if="offer.promo_code" dense align="center">
              <v-col>
                <v-text-field
                  id="offer_promo_code"
                  :model-value="offer.promo_code"
                  density="comfortable"
                  flat
                  hide-details
                  :label="isCopied ? 'Copied!' : 'Promo Code'"
                  readonly
                  tile
                  variant="solo-filled"
                  theme="light"
                >
                  <template #append-inner>
                    <v-btn
                      :icon="isCopied ? mdiCheck : mdiContentCopy"
                      size="small"
                      variant="text"
                      @click="copyPromoCode(offer.promo_code)"
                    />
                  </template>
                </v-text-field>
              </v-col>

              <v-col v-if="offer.sms_content && offer.sms_sender" cols="auto">
                <v-btn
                  block
                  class="text-none"
                  text="Text My Code"
                  tile
                  rounded="pill"
                  variant="tonal"
                  @click="openTelInputDialog"
                />
              </v-col>
            </v-row>

            <v-row
              v-if="offer.promo_cta && typeof offer.promo_cta === 'object'"
              dense
            >
              <v-col>
                <CtaButton
                  :primary-color="
                    offer.color_scheme &&
                    typeof offer.color_scheme === 'object' &&
                    offer.color_scheme.primary_button_color &&
                    typeof offer.color_scheme.primary_button_color ===
                      'object' &&
                    offer.color_scheme.primary_button_color.id
                      ? offer.color_scheme.primary_button_color.id
                      : undefined
                  "
                  :value="offer.promo_cta"
                  block
                  class="text-none"
                  target="_blank"
                  tile
                />
              </v-col>
            </v-row>
          </v-col>
        </template>

        <v-col v-if="terms" cols="12">
          <!-- eslint-disable vue/no-v-html -->
          <div class="text-caption" v-html="agreementBody(terms)"></div>
        </v-col>

        <v-col v-else cols="12"> </v-col>
      </v-row>
    </v-form>

    <TelInputDialog
      :key="telInputDialogKey"
      v-model="telInputDialog"
      :form-data="formData"
      :offer="offer"
      @close="telInputDialog = false"
    />
  </v-card-text>
</template>

<script setup lang="ts">
import type { DirectusSchema } from '#root/types/content'
import type { LeadBody } from '#root/rpc/client/lead'
import { computed, ref, toRef, reactive, useTemplateRef } from 'vue'
import { useImgResponsive } from '#root/lib/img'
import { usePageContext } from '#root/renderer/usePageContext'
import { useOffersOrThrow } from '#root/lib/useOffers'
import { emailRule, requiredRule, trueRule } from '#root/lib/form-rules'
import { agreementBody } from '#root/lib/utils'
import { mdiCheck, mdiContentCopy } from '@mdi/js'
// @ts-expect-error IntlTelInput is not typed
import IntlTelInput from 'intl-tel-input/vue'
import 'intl-tel-input/styles'

const props = defineProps<{
  offer: DirectusSchema['offer']
}>()

console.log(props.offer)

const emit = defineEmits(['submit'])

const { canonicalPaths } = usePageContext()
const { isPending, submitLead } = useOffersOrThrow()

const documentBody = document.body
const isCopied = ref(false)
const isSubmitError = ref(false)
const isValid = ref(false)
const isPhoneValid = ref(false)
const form = useTemplateRef('form')
const formData = reactive({
  agreed: false,
  name: '',
  email: '',
  phone: ''
})

const telInputDialog = ref(false)
const telInputDialogKey = ref(0)

const step = ref(props.offer.offer_type === 'promo' ? 1 : 0)
const title = computed(() =>
  step.value === 0 ? props.offer.lead_title : props.offer.promo_title
)
const description = computed(() =>
  step.value === 0
    ? props.offer.lead_description
    : props.offer.promo_description
)
const image = computed(() =>
  step.value === 0 ? props.offer.lead_image : props.offer.promo_image
)
const terms = computed(() =>
  step.value === 0 ? props.offer.lead_terms : props.offer.promo_terms
)

const aspectRatio = 1
const imgResp = useImgResponsive(
  toRef(() => ({
    aspectRatio,
    image: image.value,
    sizes: {
      xs: '180px'
    }
  }))
)

function copyPromoCode(code: string) {
  if (navigator && navigator.clipboard) navigator.clipboard.writeText(code)

  isCopied.value = true

  setTimeout(() => {
    isCopied.value = false
  }, 2000)
}

function loadUtils() {
  // @ts-expect-error IntlTelInput is not typed
  return import('intl-tel-input/utils')
}

function openTelInputDialog() {
  telInputDialogKey.value++
  telInputDialog.value = true
}

async function submit() {
  if (!form.value) return
  const result = await form.value.validate()
  if (!result.valid) return

  const body: LeadBody = {}

  if (formData.email && formData.name) {
    body.data = {
      email: formData.email,
      first_name: formData.name
    }

    if (formData.phone) body.data.phone = formData.phone
    if (props.offer.promo_code) body.data.promo_code = props.offer.promo_code

    if (props.offer.hubspot_form_id && props.offer.hubspot_portal_id) {
      body.hubspot = {
        context: {
          pageUri: canonicalPaths.absolute,
          pageName: canonicalPaths.relative
        },
        form_id: props.offer.hubspot_form_id,
        portal_id: props.offer.hubspot_portal_id
      }

      if (props.offer.hubspot_legal_consent_options) {
        body.hubspot.legalConsentOptions =
          props.offer.hubspot_legal_consent_options
      }
    }
  }

  try {
    const resp = await submitLead(body)
    if (!resp.ok) {
      isSubmitError.value = true
    } else {
      emit('submit')
      step.value++
    }
  } catch (err) {
    console.error({ err }, 'Submit error')
    isSubmitError.value = true
  }
}
</script>

<style>
.iti--container {
  z-index: 2400;
}

.offer-content .iti__tel-input {
  width: 100% !important;
}

.offer-content .iti__tel-input::placeholder {
  color: rgba(128, 128, 128, 0.9) !important;
}

.iti--container {
  color: #212121 !important;
}
</style>
